import { codigoDeEnlace } from "@gestiondoc/shared";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { esNoAutorizado, queryClient, trpc } from "./api.ts";
import { Shell } from "./componentes/Shell.tsx";
import { PaginaAcuse } from "./paginas/Acuse.tsx";
import { PaginaAlertas } from "./paginas/Alertas.tsx";
import { PaginaAuditoria } from "./paginas/Auditoria.tsx";
import { PaginaAvisos } from "./paginas/Avisos.tsx";
import { PaginaCargarFormato } from "./paginas/CargarFormato.tsx";
import { PaginaCatalogos } from "./paginas/Catalogos.tsx";
import { PaginaComparar } from "./paginas/Comparar.tsx";
import { PaginaCuenta } from "./paginas/Cuenta.tsx";
import { PaginaDiligencias } from "./paginas/Diligencias.tsx";
import { PaginaDocumento } from "./paginas/Documento.tsx";
import { PaginaEditarEncuesta, PaginaNuevaEncuesta } from "./paginas/EditarEncuesta.tsx";
import { PaginaEditarQuiz, PaginaNuevoQuiz } from "./paginas/EditarQuiz.tsx";
import { PaginaEncuestas } from "./paginas/Encuestas.tsx";
import { PaginaFirmasPersonal } from "./paginas/FirmasPersonal.tsx";
import { PaginaHoja } from "./paginas/Hoja.tsx";
import { PaginaHojas } from "./paginas/Hojas.tsx";
import { PaginaImportarHistorial } from "./paginas/ImportarHistorial.tsx";
import { PaginaJugar } from "./paginas/Jugar.tsx";
import { PaginaLecturas } from "./paginas/Lecturas.tsx";
import { PaginaLogin } from "./paginas/Login.tsx";
import { PaginaNuevoDocumento } from "./paginas/NuevoDocumento.tsx";
import { PaginaPendientes } from "./paginas/Pendientes.tsx";
import { PaginaPresentar } from "./paginas/Presentar.tsx";
import { PaginaProceso } from "./paginas/Proceso.tsx";
import { PaginaQuices } from "./paginas/Quices.tsx";
import { PaginaRedactar } from "./paginas/Redactar.tsx";
import { PaginaResponderEncuesta } from "./paginas/ResponderEncuesta.tsx";
import { PaginaResultadosEncuesta } from "./paginas/ResultadosEncuesta.tsx";
import { PaginaResultadosPartida } from "./paginas/ResultadosPartida.tsx";
import { PaginaRevisar } from "./paginas/Revisar.tsx";
import { PaginaSegundoFactor } from "./paginas/SegundoFactor.tsx";
import { PaginaUsuarios } from "./paginas/Usuarios.tsx";

/**
 * Rutas en código (sin generador). Todo lo que no es /login vive bajo `app`, que exige sesión
 * y carga el perfil una vez para el sidebar.
 */

type ContextoRutas = { queryClient: typeof queryClient; trpc: typeof trpc };

const raiz = createRootRouteWithContext<ContextoRutas>()({
  component: () => <Outlet />,
});

const login = createRoute({
  getParentRoute: () => raiz,
  path: "/login",
  // ?volverA= es adónde ir tras entrar; ?error= lo pone better-auth (o la API) al volver de Google.
  validateSearch: (s: Record<string, unknown>): { volverA?: string; error?: string } => ({
    ...(typeof s.volverA === "string" ? { volverA: s.volverA } : {}),
    ...(typeof s.error === "string" ? { error: s.error } : {}),
  }),
  component: PaginaLogin,
});

/** El perfil de la sesión; sin sesión manda al login y vuelve después. */
async function cargarPerfil(context: ContextoRutas, volverA: string) {
  try {
    return await context.queryClient.ensureQueryData(context.trpc.yo.perfil.queryOptions());
  } catch (e) {
    if (esNoAutorizado(e)) throw redirect({ to: "/login", search: { volverA } });
    throw e;
  }
}

type Perfil = Awaited<ReturnType<typeof cargarPerfil>>;

/** Fase 6: desde fuera de la red, la sesión no trabaja hasta dar el código del segundo factor. */
function faltaSegundoFactor(perfil: Perfil): boolean {
  const estado = perfil.segundoFactor.estado;
  return estado === "sin_activar" || estado === "pendiente";
}

/**
 * Exige sesión y deja el perfil en el contexto. Si a la sesión le falta el segundo factor (solo
 * pasa desde fuera de la red), manda a la pantalla del código y vuelve después.
 */
async function exigirSesion({
  context,
  location,
}: {
  context: ContextoRutas;
  location: { pathname: string };
}) {
  const perfil = await cargarPerfil(context, location.pathname);
  if (faltaSegundoFactor(perfil)) {
    throw redirect({ to: "/segundo-factor", search: { volverA: location.pathname } });
  }
  return { perfil };
}

/** Fase 6 · activar el segundo factor o dar el código, al entrar desde fuera. Sin sidebar. */
const segundoFactor = createRoute({
  getParentRoute: () => raiz,
  path: "/segundo-factor",
  validateSearch: (s: Record<string, unknown>): { volverA?: string } =>
    typeof s.volverA === "string" && s.volverA.startsWith("/") ? { volverA: s.volverA } : {},
  beforeLoad: async ({ context, search }) => {
    const perfil = await cargarPerfil(context, "/");
    // Con el código ya dado (o dentro de la red) no hay nada que hacer aquí.
    if (!faltaSegundoFactor(perfil)) throw redirect({ to: search.volverA ?? "/" });
    return { perfil };
  },
  component: PaginaSegundoFactor,
});

const app = createRoute({
  getParentRoute: () => raiz,
  id: "app",
  beforeLoad: exigirSesion,
  component: Shell,
});

/**
 * Fuera de `app` y sin exigir sesión: quien juega un quiz puede no tener cuenta. La página solo
 * habla con los procedimientos públicos de `quices`, que no sueltan nada sin el código.
 */
const jugar = createRoute({
  getParentRoute: () => raiz,
  path: "/jugar",
  validateSearch: (s: Record<string, unknown>): { codigo: string | undefined } => ({
    codigo: codigoDeEnlace(s.codigo),
  }),
  component: PaginaJugar,
});

/** Pantallas pensadas para el celular: con sesión, pero sin el sidebar (pantalla 04). */
const movil = createRoute({
  getParentRoute: () => raiz,
  id: "movil",
  beforeLoad: exigirSesion,
  component: () => <Outlet />,
});

const acuse = createRoute({
  getParentRoute: () => movil,
  path: "/acuse/$codigo",
  component: PaginaAcuse,
});

/** Lo que se proyecta: con sesión y sin sidebar, para que llene la pantalla. */
const presentar = createRoute({
  getParentRoute: () => movil,
  path: "/partidas/$id/presentar",
  component: PaginaPresentar,
});

const inicio = createRoute({
  getParentRoute: () => app,
  path: "/",
  beforeLoad: ({ context }) => {
    const propio = context.perfil.procesos.find((p) => p.esDueno) ?? context.perfil.procesos[0];
    if (propio) throw redirect({ to: "/procesos/$codigo", params: { codigo: propio.codigo } });
  },
  component: () => (
    <p className="text-texto-3">No tienes procesos asignados. Pídele acceso a un administrador.</p>
  ),
});

const proceso = createRoute({
  getParentRoute: () => app,
  path: "/procesos/$codigo",
  component: PaginaProceso,
});

const nuevoDocumento = createRoute({
  getParentRoute: () => app,
  path: "/procesos/$codigo/nuevo",
  component: PaginaNuevoDocumento,
});

const documento = createRoute({
  getParentRoute: () => app,
  path: "/documentos/$codigo",
  // ?firmar=1 abre el modal de firma al llegar (desde la bandeja o tras "Enviar a revisión").
  validateSearch: (s: Record<string, unknown>): { firmar?: boolean } =>
    s.firmar ? { firmar: true } : {},
  component: PaginaDocumento,
});

/** Pantalla 05 · el editor por bloques sobre la revisión en curso (fase 3). */
const redactar = createRoute({
  getParentRoute: () => app,
  path: "/documentos/$codigo/editar",
  component: PaginaRedactar,
});

/** Fase 4 · pantalla 07: comparar dos revisiones (?a=3&b=4; sin números, la principal y la anterior). */
const comparar = createRoute({
  getParentRoute: () => app,
  path: "/documentos/$codigo/comparar",
  validateSearch: (s: Record<string, unknown>): { a?: number; b?: number } => {
    const numero = (v: unknown) => {
      const n = Number(v);
      return Number.isInteger(n) && n > 0 ? n : undefined;
    };
    const a = numero(s.a);
    const b = numero(s.b);
    return { ...(a ? { a } : {}), ...(b ? { b } : {}) };
  },
  component: PaginaComparar,
});

/** Fase 4 · pantalla 09: revisar con marcas sobre el texto. */
const revisar = createRoute({
  getParentRoute: () => app,
  path: "/documentos/$codigo/revisar",
  component: PaginaRevisar,
});

/** Fase 3b · las hojas llenas de un formato, y una hoja para marcar. */
const hojas = createRoute({
  getParentRoute: () => app,
  path: "/documentos/$codigo/hojas",
  component: PaginaHojas,
});
const hoja = createRoute({
  getParentRoute: () => app,
  path: "/hojas/$id",
  component: PaginaHoja,
});

/** Fase 3b · los formatos que la persona llena todos los días: hoy y días anteriores. */
const diligencias = createRoute({
  getParentRoute: () => app,
  path: "/diligencias",
  component: PaginaDiligencias,
});

/** Fase 3b · un administrador carga un formato aprobado en papel, con su PDF como plantilla. */
const cargarFormato = createRoute({
  getParentRoute: () => app,
  path: "/formatos/cargar",
  component: PaginaCargarFormato,
});

/** Fase 5 · un administrador importa un documento de papel con todas sus revisiones de una vez. */
const importarHistorial = createRoute({
  getParentRoute: () => app,
  path: "/historial/importar",
  validateSearch: (s: Record<string, unknown>): { proceso?: string } =>
    typeof s.proceso === "string" && /^[A-Z]{2,4}$/.test(s.proceso) ? { proceso: s.proceso } : {},
  component: PaginaImportarHistorial,
});

const pendientes = createRoute({
  getParentRoute: () => app,
  path: "/pendientes",
  component: PaginaPendientes,
});

const lecturas = createRoute({
  getParentRoute: () => app,
  path: "/lecturas",
  component: PaginaLecturas,
});

const alertas = createRoute({
  getParentRoute: () => app,
  path: "/alertas",
  component: PaginaAlertas,
});

const avisos = createRoute({
  getParentRoute: () => app,
  path: "/avisos",
  component: PaginaAvisos,
});

/** Encuestas al personal: la bandeja, crear una, responderla y ver sus resultados. */
const encuestas = createRoute({
  getParentRoute: () => app,
  path: "/encuestas",
  component: PaginaEncuestas,
});
const encuestaNueva = createRoute({
  getParentRoute: () => app,
  path: "/encuestas/nueva",
  component: PaginaNuevaEncuesta,
});
const encuesta = createRoute({
  getParentRoute: () => app,
  path: "/encuestas/$id",
  component: PaginaResponderEncuesta,
});
const encuestaEditar = createRoute({
  getParentRoute: () => app,
  path: "/encuestas/$id/editar",
  component: PaginaEditarEncuesta,
});
const encuestaResultados = createRoute({
  getParentRoute: () => app,
  path: "/encuestas/$id/resultados",
  component: PaginaResultadosEncuesta,
});

/** Quices: la lista, el editor y los resultados de una partida. */
const quices = createRoute({
  getParentRoute: () => app,
  path: "/quices",
  component: PaginaQuices,
});
const quizNuevo = createRoute({
  getParentRoute: () => app,
  path: "/quices/nuevo",
  component: PaginaNuevoQuiz,
});
const quizEditar = createRoute({
  getParentRoute: () => app,
  path: "/quices/$id/editar",
  component: PaginaEditarQuiz,
});
const partidaResultados = createRoute({
  getParentRoute: () => app,
  path: "/partidas/$id",
  component: PaginaResultadosPartida,
});

const firmasPersonal = createRoute({
  getParentRoute: () => app,
  path: "/firmas",
  component: PaginaFirmasPersonal,
});

const auditoria = createRoute({
  getParentRoute: () => app,
  path: "/auditoria",
  component: PaginaAuditoria,
});

/** Fase 6 · "Mi cuenta": datos de la persona y su segundo factor. */
const cuenta = createRoute({
  getParentRoute: () => app,
  path: "/cuenta",
  component: PaginaCuenta,
});

/** Fase 6 · "Usuarios": un administrador da de alta, edita y desactiva a las personas. */
const usuariosAdmin = createRoute({
  getParentRoute: () => app,
  path: "/usuarios",
  component: PaginaUsuarios,
});

/** Fase 6 · "Procesos, tipos y puestos": los catálogos del sistema de gestión. */
const catalogosAdmin = createRoute({
  getParentRoute: () => app,
  path: "/catalogos",
  component: PaginaCatalogos,
});

const arbol = raiz.addChildren([
  login,
  jugar,
  segundoFactor,
  app.addChildren([
    cuenta,
    inicio,
    proceso,
    nuevoDocumento,
    documento,
    redactar,
    revisar,
    comparar,
    hojas,
    hoja,
    diligencias,
    cargarFormato,
    importarHistorial,
    pendientes,
    lecturas,
    alertas,
    avisos,
    encuestas,
    encuestaNueva,
    encuesta,
    encuestaEditar,
    encuestaResultados,
    quices,
    quizNuevo,
    quizEditar,
    partidaResultados,
    firmasPersonal,
    auditoria,
    usuariosAdmin,
    catalogosAdmin,
  ]),
  movil.addChildren([acuse, presentar]),
]);

/**
 * Errores del navegador al hablar con la API (la API todavía arrancando, caída, sin red): el
 * proxy responde vacío y fetch/JSON se quejan en inglés, y eso no le dice nada a nadie.
 */
function esErrorDeRed(error: unknown): boolean {
  return (
    error instanceof TypeError ||
    error instanceof SyntaxError ||
    (error instanceof Error && /fetch|JSON|NetworkError|Load failed/i.test(error.message))
  );
}

function ErrorDeCarga({ error }: { error: unknown }) {
  const router = useRouter();
  const mensaje =
    error instanceof Error && error.message && !esErrorDeRed(error)
      ? error.message
      : "El servidor no respondió. Prueba de nuevo en unos segundos.";
  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="max-w-[420px] rounded-tarjeta border border-borde bg-white p-6 text-center">
        <div className="mb-1 text-[15px] font-bold text-marino">No se pudo cargar</div>
        <p className="mb-4 text-texto-3">{mensaje}</p>
        <button
          type="button"
          // invalidate y no reset: vuelve a correr el loader que falló, no solo a borrar el error.
          onClick={() => void router.invalidate()}
          className="rounded-md bg-marino px-4 py-2 text-[12.5px] font-semibold text-white"
        >
          Reintentar
        </button>
      </div>
    </div>
  );
}

export const router = createRouter({
  routeTree: arbol,
  context: { queryClient, trpc },
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultErrorComponent: ({ error }) => <ErrorDeCarga error={error} />,
  defaultNotFoundComponent: () => (
    <div className="p-6 text-texto-3">Esa página no existe o no tienes acceso a ese proceso.</div>
  ),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
